'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DOTS, usePagination } from 'src/hooks/use-pagination';
import { useIsClient } from 'usehooks-ts';
import { v4 } from 'uuid';
import { cn } from '../ui-x';

type Props = {
  rangeStart: number;
  totalCount: number;
  currentPage: number;
  siblingCount: number;
  pageSize?: number;
  className?: string;
  pageColumn?: number;
  onPageChange?: (x: number) => void;
};

/**
 * The reference has no pagination — it runs on fixtures. This app has real
 * volumes, so the behaviour stays; only the presentation is restyled.
 */
const Pagination = ({
  rangeStart,
  totalCount,
  currentPage,
  siblingCount,
  pageSize = 10,
  pageColumn = 1,
  className,
  onPageChange,
}: Props) => {
  const isClient = useIsClient();
  const paginationRange = usePagination({
    currentPage,
    totalCount: totalCount / pageColumn,
    siblingCount,
    pageSize,
  });

  if (currentPage === 0 || paginationRange.length < 2) {
    return null;
  }

  const onNext = () => {
    onPageChange && onPageChange(currentPage + 1);
  };

  const onPrevious = () => {
    onPageChange && onPageChange(currentPage - 1);
  };

  let lastPage = paginationRange[paginationRange.length - 1];

  if (!isClient) return null;

  const arrow =
    'grid h-8 w-8 place-items-center rounded-md text-brand-navy transition-colors hover:bg-surface disabled:pointer-events-none disabled:text-muted-foreground/40';

  return (
    <div className={className}>
      <div className='flex items-center justify-center gap-1'>
        <button
          type='button'
          aria-label='Previous page'
          className={arrow}
          disabled={currentPage === 1}
          onClick={onPrevious}
        >
          <ChevronLeft className='h-4 w-4' />
        </button>

        {paginationRange &&
          paginationRange.map((pageNumber) => {
            if (pageNumber === DOTS) {
              return (
                <span
                  key={v4()}
                  className='grid h-8 w-8 place-items-center text-sm text-muted-foreground'
                >
                  …
                </span>
              );
            }

            return (
              <button
                type='button'
                key={pageNumber}
                aria-current={pageNumber === currentPage ? 'page' : undefined}
                onClick={() => onPageChange && onPageChange(Number(pageNumber))}
                className={cn(
                  'grid h-8 min-w-8 place-items-center rounded-md px-2 text-sm font-semibold tabular-nums transition-colors',
                  pageNumber === currentPage
                    ? 'bg-brand-navy text-white'
                    : 'text-muted-foreground hover:bg-surface hover:text-brand-navy'
                )}
              >
                {pageNumber}
              </button>
            );
          })}

        <button
          type='button'
          aria-label='Next page'
          className={arrow}
          disabled={currentPage === lastPage}
          onClick={onNext}
        >
          <ChevronRight className='h-4 w-4' />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
